import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Box, Paper, useTheme } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { Z_INDEX } from '../constants/zIndex';

interface DraggableBottomSheetProps {
    children: React.ReactNode;
    minHeight?: number; // Minimum height as percentage (0-100)
    maxHeight?: number; // Maximum height as percentage (0-100)
    initialHeight?: number; // Initial height as percentage (0-100)
    middleHeight?: number; // Middle snap point as percentage (0-100), defaults to 50
    /** Merged into the sheet `Paper` `sx` (e.g. frosted glass on homepage mockup). */
    paperSx?: SxProps<Theme>;
}

export const DraggableBottomSheet: React.FC<DraggableBottomSheetProps> = ({
    children,
    minHeight = 50,
    maxHeight = 100,
    initialHeight = 50,
    middleHeight = 50,
    paperSx,
}) => {
    const theme = useTheme();
    const [height, setHeight] = useState(initialHeight);
    const [isDragging, setIsDragging] = useState(false);
    const [startY, setStartY] = useState(0);
    const [startHeight, setStartHeight] = useState(initialHeight);
    const [endY, setEndY] = useState(0);
    const hasMovedRef = useRef(false);
    const sheetRef = useRef<HTMLDivElement>(null);

    const handleTouchStart = (e: React.TouchEvent) => {
        setIsDragging(true);
        setStartY(e.touches[0].clientY);
        setStartHeight(height);
        hasMovedRef.current = false;
    };

    const handleMouseDown = (e: React.MouseEvent) => {
        setIsDragging(true);
        setStartY(e.clientY);
        setStartHeight(height);
        hasMovedRef.current = false;
    };

    const handleMove = useCallback((clientY: number, currentStartY: number, currentStartHeight: number) => {
        if (!sheetRef.current) return;

        const containerHeight = sheetRef.current.parentElement?.clientHeight || 0;
        const deltaY = currentStartY - clientY; // Negative when dragging up
        const deltaPercent = (deltaY / containerHeight) * 100;
        const newHeight = Math.max(minHeight, Math.min(maxHeight, currentStartHeight + deltaPercent));
        
        // Mark that we've moved if the change is significant
        if (Math.abs(deltaY) > 5) {
            hasMovedRef.current = true;
        }
        
        // Track the end Y position for determining direction
        setEndY(clientY);
        setHeight(newHeight);
    }, [minHeight, maxHeight]);

    const handleTouchMove = (e: React.TouchEvent) => {
        if (!isDragging) return;
        e.preventDefault();
        handleMove(e.touches[0].clientY, startY, startHeight);
    };

    const handleTouchEnd = (e: React.TouchEvent) => {
        if (e.changedTouches && e.changedTouches.length > 0) {
            setEndY(e.changedTouches[0].clientY);
        }
        handleEnd();
    };

    const handleEnd = useCallback(() => {
        setIsDragging(false);
        
        // If it was just a click/tap (no significant movement), snap to middle
        if (!hasMovedRef.current) {
            setHeight(middleHeight);
        } else {
            // Determine drag direction based on start and end Y positions
            // If endY > startY, we dragged down (towards bottom of screen)
            // If endY < startY, we dragged up (towards top of screen)
            const draggedDown = endY > startY;
            
            if (draggedDown) {
                // Dragged down, snap to minimum (20%)
                setHeight(minHeight);
            } else {
                // Dragged up, snap to maximum (100%)
                setHeight(maxHeight);
            }
        }
        
        hasMovedRef.current = false;
    }, [minHeight, maxHeight, middleHeight, startY, endY]);

    const handleClick = useCallback((e: React.MouseEvent) => {
        // Don't snap if clicking on interactive elements (buttons, links, etc.)
        const target = e.target as HTMLElement;
        if (target.tagName === 'BUTTON' || target.tagName === 'A' || target.closest('button') || target.closest('a')) {
            return;
        }
        
        // If the sheet is at min or max, clicking anywhere should snap to middle
        if (height === minHeight || height === maxHeight) {
            setHeight(middleHeight);
        }
    }, [height, minHeight, maxHeight, middleHeight]);

    useEffect(() => {
        if (!isDragging) return;

        const handleMouseMove = (e: MouseEvent) => {
            handleMove(e.clientY, startY, startHeight);
        };

        const handleMouseUp = (e: MouseEvent) => {
            setEndY(e.clientY);
            handleEnd();
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
        document.body.style.userSelect = 'none';
        document.body.style.cursor = 'grabbing';

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.body.style.userSelect = '';
            document.body.style.cursor = '';
        };
    }, [isDragging, startY, startHeight, handleMove, handleEnd]);

    return (
        <Paper
            ref={sheetRef}
            elevation={0}
            sx={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                height: `${height}%`,
                background: theme.palette.background.paper,
                borderTopLeftRadius: { md: 18, lg: 22 },
                borderTopRightRadius: { md: 18, lg: 22 },
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
                transition: isDragging ? 'none' : 'height 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                touchAction: 'none',
                zIndex: Z_INDEX.BOTTOM_SHEET,
                boxShadow: '0 -12px 40px rgba(0,0,0,0.12)',
                ...(paperSx ?? {}),
            }}
        >
            {/* Drag Handle */}
            <Box
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                onMouseDown={handleMouseDown}
                sx={{
                    width: '100%',
                    py: 1.5,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    cursor: isDragging ? 'grabbing' : 'grab',
                    touchAction: 'none',
                    userSelect: 'none',
                }}
            >
                <Box
                    sx={{
                        width: { md: 40, lg: 48 },
                        height: { md: 4, lg: 5 },
                        borderRadius: 2,
                        background: theme.palette.mode === 'dark' 
                            ? 'rgba(255, 255, 255, 0.3)' 
                            : 'rgba(0, 0, 0, 0.2)',
                    }}
                />
            </Box>

            {/* Content */}
            <Box
                onClick={handleClick}
                sx={{
                    flex: 1,
                    overflow: 'auto',
                    px: { md: 2.5, lg: 3 },
                    pb: { md: 2.5, lg: 3 },
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    cursor: (height === minHeight || height === maxHeight) ? 'pointer' : 'default',
                }}
            >
                {children}
            </Box>
        </Paper>
    );
};

